import { readMemberResource, writeMemberResource } from './resourceCache'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export const ACCOUNT_RESOURCE_KEY = 'account-current-statement'

export function parseAccountStatementResponse(response, body, updatedAt = Date.now()) {
  const rawTotal = body?.data?.account?.currentBalance
  if (!response?.ok || body?.ok !== true || !body?.data?.account || rawTotal === null || rawTotal === undefined || rawTotal === '' || !Number.isFinite(Number(rawTotal))) {
    const error = new Error(body?.error || 'invalid_statement_response')
    error.status = response?.status || null
    error.machineCode = body?.error || 'invalid_statement_response'
    throw error
  }
  return {
    status: 'ready', totalAmount: Number(rawTotal), currentBalance: String(rawTotal), availableCredit: String(body.data.account.availableCredit ?? '0'), currency: body.data.account.currency || null, source: body.data.account.source,
    itemCount: Number.isFinite(Number(body.data.summary?.item_count)) ? Number(body.data.summary.item_count) : 0,
    attributable_charges:Array.isArray(body.data.attributable_charges)?body.data.attributable_charges:[],
    period: body.data.period || null, categories: Array.isArray(body.data.categories) ? body.data.categories : [], items: Array.isArray(body.data.items) ? body.data.items : [],
    payment: { enabled: body.data.privana_pay?.enabled === true, onboardingComplete: body.data.privana_pay?.onboarding_complete === true },
    updatedAt, stale: false, cacheState: 'fresh', errorCode: null,
  }
}

export function readAccountResource(scope) {
  const entry = readMemberResource(scope, ACCOUNT_RESOURCE_KEY, { ttlMs: 5 * 60 * 1000 })
  return entry ? { ...entry.data, updatedAt: entry.updatedAt, stale: entry.stale, cacheState: entry.stale ? 'stale' : 'hit' } : null
}

export function writeAccountResource(scope, resource) {
  return writeMemberResource(scope, ACCOUNT_RESOURCE_KEY, { ...resource, updatedAt: resource.updatedAt || Date.now(), stale: false, cacheState: 'fresh' })
}

export function useCanonicalAccountResource({ scope, identityKey, enabled, memberPortalApiGet }) {
  const cached = useMemo(() => readAccountResource(scope), [scope])
  const [state, setState] = useState(() => ({ identityKey, resource: cached, loading: Boolean(enabled), error: '' }))
  const activeIdentityRef = useRef(identityKey); activeIdentityRef.current = identityKey
  const requestRef = useRef(null)
  const resource = state.identityKey === identityKey ? state.resource : cached
  const loading = state.identityKey === identityKey ? state.loading : Boolean(enabled)
  const error = state.identityKey === identityKey ? state.error : ''

  const refresh = useCallback(({ reason = 'background' } = {}) => {
    if (!enabled) return Promise.resolve(null)
    if (requestRef.current?.identityKey === identityKey) return requestRef.current.promise
    const controller = new AbortController()
    console.info('[members-account-balance]', { event: 'request_started', clubCode: scope.clubCode, sourceEndpoint: 'account/current-statement' })
    setState(current => ({ identityKey, resource: current.identityKey === identityKey ? current.resource : readAccountResource(scope), loading: true, error: '' }))
    const promise = memberPortalApiGet(`/api/members-portal/${scope.clubCode}/account/current-statement`, { signal: controller.signal })
      .then(async response => parseAccountStatementResponse(response, await response.json().catch(() => null)))
      .then(next => {
        if (activeIdentityRef.current !== identityKey) {
          console.info('[members-account-balance]', { event: 'stale_identity_response_rejected', clubCode: scope.clubCode })
          return null
        }
        writeAccountResource(scope, next)
        console.info('[members-account-balance]', { event: 'response_received', clubCode: scope.clubCode, source: next.source })
        setState({ identityKey, resource: next, loading: false, error: '' })
        return next
      })
      .catch(cause => {
        if (activeIdentityRef.current === identityKey && cause?.name !== 'AbortError') setState(current => ({ identityKey, resource: current.identityKey === identityKey ? current.resource : null, loading: false, error: reason === 'retry' ? 'Your balance could not be refreshed.' : 'Account balance is temporarily unavailable.' }))
        throw cause
      })
      .finally(() => { if (requestRef.current?.promise === promise) requestRef.current = null })
    requestRef.current = { identityKey, promise, cancel: () => controller.abort() }
    return promise
  }, [enabled, identityKey, memberPortalApiGet, scope])

  useEffect(() => {
    const cachedResource = readAccountResource(scope)
    console.info('[members-account-balance]', { event: cachedResource ? 'cache_hit' : 'cache_miss', clubCode: scope.clubCode })
    setState({ identityKey, resource: cachedResource, loading: Boolean(enabled), error: '' })
    if (enabled) refresh().catch(() => {})
    return () => requestRef.current?.identityKey === identityKey && requestRef.current.cancel()
  }, [enabled, identityKey, refresh, scope])

  const clear = useCallback(() => {
    requestRef.current?.cancel()
    setState({ identityKey, resource: null, loading: false, error: '' })
  }, [identityKey])

  return { resource, loading, refreshing: loading && Boolean(resource), error, refresh, retry: () => refresh({ reason: 'retry' }), clear }
}
